import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '@frontend/lib/cn';

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: { label: string; to?: string }[];
  className?: string;
}): JSX.Element {
  return (
    <header className={cn('mb-4 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        {breadcrumbs !== undefined && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-1 flex flex-wrap items-center gap-1 text-body">
            {breadcrumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 && <span className="text-content-secondary">/</span>}
                {crumb.to === undefined ? (
                  <span className="text-content-secondary">{crumb.label}</span>
                ) : (
                  <NavLink to={crumb.to} className="text-content-link">
                    {crumb.label}
                  </NavLink>
                )}
              </span>
            ))}
          </nav>
        )}
        {/*
          A short coral rule under the title — identity, not an affordance, and `aria-hidden`
          because it says nothing a screen reader needs.

          It earns its place under the accepted system's "one coral moment per screen" rule
          only where the page has no other claim on the accent. Where a screen has a real
          coral moment of its own — the leave-lapse warning in mockup 9a, a pending approval —
          that moment is the one that matters and this rule is the thing to drop, not the
          other way round.

          No `font-semibold`: the accepted title weight is carried by `text-title-*` (800),
          so adding a weight utility here would fight the scale rather than reinforce it.
        */}
        {/* Responsive per the accepted spec: 20px on phone, 26px at tablet, 30px at desktop —
            not a single fluid size (tailwind.config.ts fontSize note). */}
        <h1 className="truncate text-title-sm tablet:text-title desktop:text-title-lg text-content">{title}</h1>
        <div aria-hidden className="mt-1.5 h-[2px] w-10 rounded-sm bg-brand" />
        {/* Page background: secondary, never tertiary — tertiary fails AA off-surface. */}
        {description !== undefined && description !== null && (
          <p className="mt-1 max-w-prose text-pretty text-body text-content-secondary">{description}</p>
        )}
      </div>
      {actions !== undefined && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function PageBody({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}

export function Tabs({
  tabs,
  className,
}: {
  tabs: { label: string; to: string; end?: boolean; badge?: ReactNode }[];
  className?: string;
}): JSX.Element {
  return (
    <nav className={cn('flex gap-1 overflow-x-auto border-b border-line no-scrollbar', className)}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              '-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-row font-medium transition-token',
              isActive
                ? 'border-interactive text-content'
                : 'border-transparent text-content-secondary hover:border-line-strong hover:text-content',
            )
          }
        >
          {tab.label}
          {tab.badge}
        </NavLink>
      ))}
    </nav>
  );
}

/** Small count pill for tab badges and nav items. */
export function CountPill({ count, tone = 'neutral' }: { count: number; tone?: 'neutral' | 'alert' }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'inline-flex min-w-[16px] items-center justify-center rounded-full px-1 text-body font-semibold tabular',
        tone === 'alert' ? 'bg-error text-content-inverse' : 'bg-surface-muted text-content-secondary',
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Label/value pair used across every detail screen. */
export function DetailItem({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-body uppercase tracking-[0.04em] text-content-tertiary">{label}</dt>
      <dd className="mt-0.5 text-row text-content">{children}</dd>
    </div>
  );
}

export function DetailGrid({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <dl className={cn('grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3', className)}>{children}</dl>
  );
}

/** KPI tile. `value` is pre-formatted by the caller — this never formats money itself. */
export function StatCard({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  'data-testid': testId,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'positive' | 'warning' | 'negative';
  icon?: ReactNode;
  'data-testid'?: string;
}): JSX.Element {
  const toneClass =
    tone === 'positive'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'negative'
          ? 'text-error'
          : 'text-content';
  return (
    // Oversized mono numeral, per the accepted KPI tile (mockup 2a): the label is a plain-case
    // caption, the number is the thing the eye lands on — 38px IBM Plex Mono, not a bolded
    // 20px sans-serif figure fighting the label for attention.
    <div className="surface-card p-4" data-testid={testId ?? `${label.toLowerCase().replace(/\s+/g, '-')}-card`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-body text-content-tertiary">{label}</p>
        {icon !== undefined && <span className="text-content-tertiary">{icon}</span>}
      </div>
      <p className={cn('mt-2.5 font-mono text-metric-lg tabular', toneClass)}>{value}</p>
      {hint !== undefined && <p className="mt-0.5 text-body text-content-tertiary">{hint}</p>}
    </div>
  );
}

export function Banner({
  tone,
  title,
  children,
  action,
  className,
  'data-testid': testId,
}: {
  tone: 'info' | 'success' | 'warning' | 'error';
  title?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}): JSX.Element {
  const styles: Record<typeof tone, string> = {
    info: 'border-info-border bg-info-bg text-content',
    success: 'border-success-border bg-success-bg text-content',
    warning: 'border-warning-border bg-warning-bg text-content',
    error: 'border-error-border bg-error-bg text-content',
  };
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      data-testid={testId}
      className={cn('rounded-md border px-3 py-2.5', styles[tone], className)}
    >
      {title !== undefined && <p className="text-row font-semibold">{title}</p>}
      <div className={cn('text-row', title === undefined ? '' : 'mt-0.5')}>{children}</div>
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}
